import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { IActionFormProps, isEmail } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label, SelectDropdown, Switch } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { isEmpty } from '@/shared/utils'
import { ActionInputV3 } from '@/components/ActionInputV3/ActionInputV3'

import { TableSelect } from '../../shared/TableSelect'

// TODO: This is a temporary component for the Gmail plugin. It will be replaced with the actual Gmail plugin.
// Currently not being used.

export default function ActFormGmailPlugin({ onSubmit, onDismiss, initialData, triggerSource }: IActionFormProps) {
  const { tables } = useDbClientContext()

  // create table options
  const tblOptions = tables
  const shouldShowTable = ['menu-item'].includes(triggerSource)
  const defaultTableId = shouldShowTable ? (!!tblOptions.length ? tblOptions[0]?.id : '') : undefined

  // Mock email options - TODO: Replace with real Gmail account fetching
  const emailAccounts = [
    { id: 'hesaubud@gmail.com', email: 'hesaubud@gmail.com', name: 'Hesa Wellness Spa Ubud' },
    { id: 'kmegayani@sensatia.com', email: 'kmegayani@sensatia.com', name: 'Sensatia Botanicals' },
    { id: 'admin@company.com', email: 'admin@company.com', name: 'Company Admin' },
    { id: 'support@business.com', email: 'support@business.com', name: 'Business Support' },
  ]

  const emailOptions = [
    ...emailAccounts,
    { id: 'add-account', email: 'add-account', name: '+ Add account', isAddAccount: true },
  ]

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      tableId: initialData?.tableId ?? defaultTableId ?? '',
      from: initialData?.from || emailOptions[0]?.email || '',
      to: initialData?.to || {},
      cc: initialData?.cc || {},
      bcc: initialData?.bcc || {},
      subject: initialData?.subject || {},
      body: initialData?.body || {},
      htmlBody: initialData?.htmlBody || {},
      useHtmlBody: initialData?.useHtmlBody ?? false,
    },
    validate(data, setError) {
      // Validate From email (now a simple string from dropdown)
      let fromError = !data.from?.trim().length ? 'From email cannot be empty' : null

      setError('from', fromError)

      // Validate To email
      let toValue = data.to.value
      let isToManualInput = !data.to.tableId && !data.to.dataType
      let toError = !toValue?.trim().length
        ? 'To email cannot be empty'
        : isToManualInput && !isEmail(data.to.value)
          ? 'Please enter a valid to email'
          : null

      setError('to', toError)

      // Validate CC email if provided
      let ccValue = data.cc.value
      let isCcManualInput = !data.cc.tableId && !data.cc.dataType
      let ccError =
        ccValue?.trim().length && isCcManualInput && !isEmail(data.cc.value) ? 'Please enter a valid CC email' : null

      setError('cc', ccError)

      // Validate BCC email if provided
      let bccValue = data.bcc.value
      let isBccManualInput = !data.bcc.tableId && !data.bcc.dataType
      let bccError =
        bccValue?.trim().length && isBccManualInput && !isEmail(data.bcc.value)
          ? 'Please enter a valid BCC email'
          : null

      setError('bcc', bccError)
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const from = form.getValues('from')
  const to = form.getValues('to')
  const tableId = form.getValues('tableId')
  const useHtmlBody = form.getValues('useHtmlBody')

  const invalidForm = !from?.trim().length || isEmpty(to) || !!form.errors || (shouldShowTable && isEmpty(tableId))

  return (
    <div>
      <div className='p-4'>
        {shouldShowTable && (
          <Field className='min-h-0'>
            <Label required>Table</Label>

            <TableSelect
              options={tblOptions}
              value={tblOptions.find((tb) => tb.id === form.getValues('tableId'))}
              onChange={(tb) => {
                form.setValues('tableId', tb.id)
              }}
              error={form.errors?.tableId}
            />
          </Field>
        )}

        <Field>
          <Label required>From</Label>
          <div className='flex-1'>
            <SelectDropdown
              multiple={false}
              options={emailOptions}
              optionLabel={(opt) => ((opt as any).isAddAccount ? opt.name : `${opt.name} <${opt.email}>`)}
              value={emailOptions.find(({ email }) => email === form.getValues('from'))}
              renderOption={(opt) => {
                if ((opt as any).isAddAccount) {
                  return (
                    <div className='flex py-2 items-center text-sm text-gray-700 dark:text-gray-200 rounded'>
                      <span className='font-medium text-gray-900 dark:text-gray-100'>{opt.name}</span>
                    </div>
                  )
                }
                return (
                  <div className='flex flex-col'>
                    <span className='font-medium text-gray-900 dark:text-gray-100'>{opt.name}</span>
                    <span className='text-sm text-gray-500 dark:text-gray-400'>{opt.email}</span>
                  </div>
                )
              }}
              onChange={(selectedEmail) => {
                if ((selectedEmail as any).isAddAccount) {
                  // TODO: Handle redirect to add account
                  console.log('Redirect to add account')
                  return
                }
                form.setValues('from', selectedEmail.email)
              }}
            />
            <div className='mt-1 text-m-sm red h-[17px]'>{form.errors?.from}</div>
          </div>
        </Field>

        <Field>
          <Label required>To</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter to email'
            value={form.getValues('to')}
            columnTypes={[ColumnTypes.Email]}
            onChange={({ ...value }) => {
              form.setValues('to', value)
            }}
            error={form.errors?.to}
          />
        </Field>

        <Field>
          <Label>Cc</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter CC email'
            value={form.getValues('cc')}
            columnTypes={[ColumnTypes.Email]}
            onChange={({ ...value }) => {
              form.setValues('cc', value)
            }}
            error={form.errors?.cc}
          />
        </Field>

        <Field>
          <Label>Bcc</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter BCC email'
            value={form.getValues('bcc')}
            columnTypes={[ColumnTypes.Email]}
            onChange={({ ...value }) => {
              form.setValues('bcc', value)
            }}
            error={form.errors?.bcc}
          />
        </Field>

        <Field>
          <Label>Subject</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter subject'
            value={form.getValues('subject')}
            columnTypes={[ColumnTypes.Text]}
            onChange={({ ...value }) => {
              form.setValues('subject', value)
            }}
            error={form.errors?.subject}
          />
        </Field>

        <Field>
          <Label>Body</Label>
          <ActionInputV3
            options={initialData?.actionOptions}
            isActionWorkflow={initialData?.isActionWorkflow}
            globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
            placeholder='Enter email body'
            inputType='textarea'
            value={form.getValues('body')}
            columnTypes={[ColumnTypes.Text, ColumnTypes.RichText, ColumnTypes.Join]}
            onChange={({ ...value }) => {
              form.setValues('body', value)
            }}
            error={form.errors?.body}
          />
        </Field>

        {/* HTML Body Toggle */}
        <Field>
          <div className='flex items-center gap-3'>
            <div className='flex-shrink-0'>
              <Switch checked={useHtmlBody} onClick={(isChecked) => form.setValues('useHtmlBody', isChecked)} />
            </div>
            <Label className='text-sm font-medium text-gray-900 dark:text-white mb-0 whitespace-nowrap'>
              <Trans>Use HTML Body</Trans>
            </Label>
          </div>
        </Field>

        {/* HTML Body Field - shown when toggle is enabled */}
        {useHtmlBody && (
          <Field>
            <Label>HTML Body</Label>
            <ActionInputV3
              options={initialData?.actionOptions}
              isActionWorkflow={initialData?.isActionWorkflow}
              globalTableId={shouldShowTable ? form.getValues('tableId') : undefined}
              placeholder='Enter HTML body'
              inputType='textarea'
              value={form.getValues('htmlBody')}
              columnTypes={[ColumnTypes.Text, ColumnTypes.RichText, ColumnTypes.Join]}
              onChange={({ ...value }) => {
                form.setValues('htmlBody', value)
              }}
              error={form.errors?.htmlBody}
            />
          </Field>
        )}
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
